import type {
  JobAccepted,
  JobRecord,
  MigrationRequest,
  MigrationResult,
  Planner,
  ProgressEvent,
} from "../types";

/**
 * How often to ask for a job's progress.
 *
 * Measured against a real 59-second migration emitting 33 events: at two
 * seconds a line was on screen 1.16s after it happened on average and up to
 * 2.0s late; at one second that halves to 0.59s average, 0.99s worst, for 59
 * requests instead of 30. Thirty extra key-lookups per migration is not worth
 * weighing against the difference between "it is working" and "is it stuck".
 *
 * Half a second halves it again, but 117 requests buys a 330ms improvement
 * nobody can feel, so this is where it stops.
 */
const POLL_MS = 1000;

/** Give up after this long, so a lost job cannot spin forever. */
const MAX_WAIT_MS = 15 * 60 * 1000;

const wait = (ms: number) => new Promise((resolve) => setTimeout(resolve, ms));

/**
 * Starts a migration and follows it to completion.
 *
 * The server accepts the work and answers with a job id; the migration itself
 * runs somewhere this request is not waiting on. That is what lets it take
 * the 40-60 seconds it needs without meeting an API Gateway timeout.
 *
 * Progress is the job's event log, which only grows - so each poll replays
 * anything new since the last one rather than the client tracking offsets.
 */
export async function runMigrationJob(
  planner: Planner,
  body: MigrationRequest,
  onProgress: (event: ProgressEvent) => void,
  signal?: AbortSignal,
): Promise<MigrationResult> {
  const accepted = await post(`/api/migrate/${planner}`, body);
  const deadline = Date.now() + MAX_WAIT_MS;
  let seen = 0;

  /*
   * Ask straight away, then settle into the poll interval.
   *
   * The server writes the job record before it answers with the id, so there
   * is always something to read - waiting a full interval first only delays
   * the first sign of life by two seconds on top of however long the request
   * took, which is exactly the part of the run that looks stalled.
   */
  for (;;) {
    if (signal?.aborted) throw new Error("Cancelled.");

    const job = await get<JobRecord>(accepted.poll);

    // The log is append-only, so everything past the last index is new.
    for (const event of job.progress.slice(seen)) onProgress(event);
    seen = job.progress.length;

    if (job.finished) {
      if (job.result) return job.result;
      throw new Error(job.error ?? "The migration failed without a result.");
    }

    if (Date.now() > deadline) {
      throw new Error(
        `Still running after ${Math.round(MAX_WAIT_MS / 60000)} minutes. ` +
          `Check job ${job.jobId}.`,
      );
    }

    await wait(POLL_MS);
  }
}

async function post(path: string, body: unknown): Promise<JobAccepted> {
  const response = await fetch(path, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(body),
  });
  const payload = await response.json().catch(() => null);

  // 202 is the expected answer: accepted, not finished.
  if (!response.ok) {
    throw new Error(
      (payload as { error?: string })?.error ??
        `Could not start the migration (${response.status}).`,
    );
  }
  return payload as JobAccepted;
}

async function get<T>(path: string): Promise<T> {
  const response = await fetch(path);
  const payload = await response.json().catch(() => null);
  if (!response.ok) {
    throw new Error(
      (payload as { error?: string })?.error ?? `Request failed (${response.status}).`,
    );
  }
  return payload as T;
}
